import {
  INDIAN_POKER_MIN_STARTING_CHIPS,
  INDIAN_POKER_STARTING_CHIPS,
  type IndianPokerPlayer,
  type IndianPokerViewerCard,
  type IndianPokerViewerState,
} from "@cloudnest/indian-poker-engine";
import { useEffect, useRef, useState } from "react";
import { buildLobbyPath, navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import {
  indianPokerMultiplayerActions,
  useIndianPokerMultiplayerStore,
} from "./multiplayer-game-store";

interface PlayerView {
  readonly player: IndianPokerPlayer;
  readonly card: IndianPokerViewerCard | null;
}

type PlayerNameResolver = (playerId: string) => string;

const INDIAN_POKER_GAME_TYPE = "indian-poker";
const ROUND_RESULT_REVEAL_LOCK_MS = 2200;

type SeatResult = "winner" | "loser";

function getRoomSeed(config: Record<string, unknown>, roomId: string): string {
  const configuredSeed = config.seed;
  if (typeof configuredSeed === "string" && configuredSeed.trim().length > 0) {
    return configuredSeed.trim();
  }
  return `indian-poker:${roomId}`;
}

function getRoomStartingChips(config: Record<string, unknown>): number {
  const configuredChips = config.startingChips;
  if (
    typeof configuredChips !== "number" ||
    !Number.isFinite(configuredChips)
  ) {
    return INDIAN_POKER_STARTING_CHIPS;
  }
  return Math.max(INDIAN_POKER_MIN_STARTING_CHIPS, Math.trunc(configuredChips));
}

function getRoomPlayerName(
  room: ReturnType<typeof useLobby>["currentRoom"],
  playerId: string,
): string {
  return room
    ? getParticipantDisplayName(room, playerId, "플레이어")
    : playerId;
}

function getCard(
  state: IndianPokerViewerState,
  playerId: string,
): IndianPokerViewerCard | null {
  return state.round.cards.find((card) => card.playerId === playerId) ?? null;
}

function getPlayerViews(
  state: IndianPokerViewerState,
  myPlayerId: string | null,
): {
  readonly self: PlayerView | null;
  readonly opponent: PlayerView | null;
} {
  const selfPlayer =
    state.players.find((player) => player.playerId === myPlayerId) ?? null;
  const opponentPlayer =
    state.players.find((player) => player.playerId !== myPlayerId) ?? null;

  return {
    self: selfPlayer
      ? { player: selfPlayer, card: getCard(state, selfPlayer.playerId) }
      : null,
    opponent: opponentPlayer
      ? {
          player: opponentPlayer,
          card: getCard(state, opponentPlayer.playerId),
        }
      : null,
  };
}

function getObserverPlayerViews(state: IndianPokerViewerState): {
  readonly self: PlayerView | null;
  readonly opponent: PlayerView | null;
} {
  const [firstPlayer, secondPlayer] = state.players;

  return {
    self: firstPlayer
      ? { player: firstPlayer, card: getCard(state, firstPlayer.playerId) }
      : null,
    opponent: secondPlayer
      ? { player: secondPlayer, card: getCard(state, secondPlayer.playerId) }
      : null,
  };
}

function getResultText(state: IndianPokerViewerState): string {
  if (state.round.phase === "GAME_OVER") {
    return state.winnerPlayerId
      ? "게임 종료 · 승자가 결정되었습니다"
      : "게임 종료";
  }

  if (state.round.resultReason === "TIE") {
    return `무승부 · 팟 ${state.round.carryoverPot}칩 이월`;
  }
  if (state.round.resultReason === "TEN_FOLD_PENALTY") {
    return "10 카드 폴드 페널티가 적용되었습니다";
  }
  if (state.round.resultReason === "FOLD") {
    return "폴드로 라운드가 종료되었습니다";
  }
  return `라운드 승리 · 팟 ${state.round.pot}칩 지급`;
}

function willShowGameOverAfterResult(state: IndianPokerViewerState): boolean {
  return (
    state.round.phase === "ROUND_RESULT" &&
    state.round.resultReason !== undefined &&
    state.round.resultReason !== "TIE" &&
    state.players.some((player) => player.chips <= 0)
  );
}

function getSeatResult(
  state: IndianPokerViewerState,
  playerId: string | undefined,
): SeatResult | undefined {
  if (
    !playerId ||
    state.round.phase === "BETTING" ||
    state.round.resultReason === "TIE" ||
    !state.round.winnerPlayerId
  ) {
    return undefined;
  }

  return state.round.winnerPlayerId === playerId ? "winner" : "loser";
}

function ForeheadCard({
  card,
  owner,
}: {
  readonly card: IndianPokerViewerCard | null;
  readonly owner: "self" | "opponent";
}) {
  if (!card || card.hidden || !card.card) {
    return (
      <div
        className="indian-poker-card back"
        data-hidden="true"
        data-testid={`indian-poker-card-${owner}`}
      >
        내 카드
        <br />
        가림
      </div>
    );
  }
  return (
    <div
      className="indian-poker-card face"
      data-hidden="false"
      data-testid={`indian-poker-card-${owner}`}
      data-value={card.card.value}
    >
      {card.card.value}
    </div>
  );
}

function PlayerSeat({
  view,
  owner,
  label,
  active,
  result,
  name,
  observerStatus,
}: {
  readonly view: PlayerView | null;
  readonly owner: "self" | "opponent";
  readonly label: string;
  readonly active: boolean;
  readonly result?: SeatResult;
  readonly name: string;
  readonly observerStatus?: boolean;
}) {
  const statusLabel = active
    ? "액션 차례"
    : result === "winner"
      ? "승리"
      : result === "loser"
        ? "패배"
        : null;

  return (
    <section
      className={`indian-poker-seat indian-poker-seat-card-focus ${
        active ? "active" : ""
      } ${result ? `result-${result}` : ""}`}
      data-active={active ? "true" : "false"}
      data-result={result ?? "none"}
      data-testid={`indian-poker-seat-${owner}`}
    >
      <div className="indian-poker-seat-name-column">
        <div className="indian-poker-seat-label-row">
          <strong>{label}</strong>
          {observerStatus && (
            <span
              className="indian-poker-turn-badge"
              data-testid="indian-poker-observer-status"
            >
              관전 중
            </span>
          )}
          {statusLabel && (
            <span
              className="indian-poker-turn-badge"
              data-result={result ?? "none"}
              data-testid={
                active
                  ? `indian-poker-turn-badge-${owner}`
                  : `indian-poker-result-badge-${owner}`
              }
            >
              {statusLabel}
            </span>
          )}
        </div>
        <span data-testid={`indian-poker-name-${owner}`}>{name}</span>
      </div>
      <ForeheadCard card={view?.card ?? null} owner={owner} />
      <div className="indian-poker-seat-chip-column">
        <div
          className="indian-poker-chip-count"
          data-testid={`indian-poker-chips-${owner}`}
        >
          <span>보유</span>
          <strong className="indian-poker-chip-value">
            {view?.player.chips ?? 0}
          </strong>
          <span>칩</span>
        </div>
      </div>
    </section>
  );
}

function getAvailableActionsText(state: IndianPokerViewerState): string {
  const activePlayer = state.players.find(
    (player) => player.playerId === state.round.activePlayerId,
  );
  const opponent = state.players.find(
    (player) => player.playerId !== state.round.activePlayerId,
  );
  if (!activePlayer || !opponent) {
    return "레이즈 / 폴드 가능";
  }

  const requiredCallAmount = Math.max(
    0,
    opponent.currentBet - activePlayer.currentBet,
  );
  const minRaiseTotal = opponent.currentBet + 1;
  const maxRaiseTotal = Math.min(
    activePlayer.currentBet + activePlayer.chips,
    opponent.currentBet + opponent.chips,
  );
  const canCall =
    requiredCallAmount > 0 && requiredCallAmount <= activePlayer.chips;
  const canRaise = minRaiseTotal <= maxRaiseTotal;

  if (canCall && canRaise) return "콜 / 레이즈 / 폴드 가능";
  if (canCall) return "콜 / 폴드 가능";
  if (canRaise) return "레이즈 / 폴드 가능";
  return "자동 공개 대기";
}

function getLastBettingActionText(
  state: IndianPokerViewerState,
  getPlayerName: PlayerNameResolver,
  myPlayerId: string | null,
): string | null {
  const lastActorId = state.round.actedPlayerIds.at(-1);
  if (!lastActorId) return null;

  const activePlayer = state.players.find(
    (player) => player.playerId === state.round.activePlayerId,
  );
  const lastActor = state.players.find(
    (player) => player.playerId === lastActorId,
  );
  if (
    !activePlayer ||
    !lastActor ||
    lastActor.playerId === activePlayer.playerId
  ) {
    return null;
  }

  const actionLabel =
    lastActor.currentBet > activePlayer.currentBet ? "레이즈" : "응답";
  const actorName =
    lastActor.playerId === myPlayerId
      ? "나"
      : getPlayerName(lastActor.playerId);
  return `${actorName} ${actionLabel}`;
}

function BetLine({
  owner,
  label,
  amount,
  hidden,
}: {
  readonly owner: "self" | "opponent";
  readonly label: string;
  readonly amount: number;
  readonly hidden?: boolean;
}) {
  return (
    <div
      aria-hidden={hidden ? "true" : undefined}
      className={`indian-poker-bet-line ${hidden ? "empty" : ""}`}
      data-owner={owner}
      data-visible={hidden ? "false" : "true"}
      data-testid={`indian-poker-bet-${owner}`}
    >
      <span className="indian-poker-chip-dot" />
      <span>{label}</span>
      <strong className="indian-poker-number">{amount}</strong>
    </div>
  );
}

function getLastRaiseAmount(state: IndianPokerViewerState): number | null {
  const lastActorId = state.round.actedPlayerIds.at(-1);
  if (!lastActorId || state.round.phase !== "BETTING") return null;

  const activePlayer = state.players.find(
    (player) => player.playerId === state.round.activePlayerId,
  );
  const lastActor = state.players.find(
    (player) => player.playerId === lastActorId,
  );
  if (!activePlayer || !lastActor) return null;

  const amount = lastActor.currentBet - activePlayer.currentBet;
  return amount > 0 ? amount : null;
}

function getDecisionCaption(
  state: IndianPokerViewerState,
  getPlayerName: PlayerNameResolver,
  myPlayerId: string | null,
): string {
  if (state.round.phase !== "BETTING") {
    return getResultText(state);
  }

  const lastActorId = state.round.actedPlayerIds.at(-1);
  if (!lastActorId) {
    return "상대 카드를 보고 선택하세요";
  }

  if (lastActorId === myPlayerId) {
    return "내가 레이즈했습니다";
  }

  return `${getPlayerName(lastActorId)}님이 레이즈했습니다`;
}

function DecisionPanel({
  state,
  selfBet,
  opponentBet,
  betGap,
  getPlayerName,
  myPlayerId,
  isMyTurn,
}: {
  readonly state: IndianPokerViewerState;
  readonly selfBet: number;
  readonly opponentBet: number;
  readonly betGap: number;
  readonly getPlayerName: PlayerNameResolver;
  readonly myPlayerId: string | null;
  readonly isMyTurn: boolean;
}) {
  const isResolved = state.round.phase !== "BETTING";
  const lastRaiseAmount = getLastRaiseAmount(state);
  const leftLabel = lastRaiseAmount ? "마지막 레이즈" : "상대";
  const leftAmount = lastRaiseAmount ?? opponentBet;
  const rightLabel = isResolved ? "나" : "배팅 격차";
  const rightAmount = isResolved ? selfBet : betGap;
  const responseLabel = isResolved
    ? "결과 공개"
    : isMyTurn
      ? "내 응답 필요"
      : "상대 선택 대기";

  return (
    <section
      className="indian-poker-decision-panel"
      data-bet-gap={betGap}
      data-carryover-pot={state.round.carryoverPot}
      data-pot={state.round.pot}
      data-resolved={isResolved ? "true" : "false"}
      data-testid="indian-poker-decision-panel"
    >
      <div className="indian-poker-decision-caption">
        <span>{getDecisionCaption(state, getPlayerName, myPlayerId)}</span>
        <strong>{responseLabel}</strong>
      </div>
      <div className="indian-poker-decision-main">
        <BetLine
          owner="opponent"
          label={leftLabel}
          amount={leftAmount}
          hidden={isResolved}
        />
        <section
          className="indian-poker-pot"
          data-bet-gap={betGap}
          data-carryover-pot={state.round.carryoverPot}
          data-pot={state.round.pot}
          data-resolved={isResolved ? "true" : "false"}
          data-testid="indian-poker-pot"
        >
          <span
            className="indian-poker-pot-stack"
            data-testid="indian-poker-pot-total"
          >
            팟{" "}
            <strong className="indian-poker-number">{state.round.pot}</strong>
          </span>
          {!isResolved && betGap > 0 && (
            <span
              className="indian-poker-pot-note"
              data-testid="indian-poker-bet-gap"
            >
              격차 {betGap}
            </span>
          )}
          {state.round.carryoverPot > 0 && (
            <span
              className="indian-poker-pot-note carry"
              data-testid="indian-poker-carryover"
            >
              이월 {state.round.carryoverPot}
            </span>
          )}
        </section>
        <BetLine
          owner="self"
          label={rightLabel}
          amount={rightAmount}
          hidden={isResolved}
        />
      </div>
    </section>
  );
}

function ActionMessage({
  state,
  isMyTurn,
  activeName,
  getPlayerName,
  myPlayerId,
}: {
  readonly state: IndianPokerViewerState;
  readonly isMyTurn: boolean;
  readonly activeName: string;
  readonly getPlayerName: PlayerNameResolver;
  readonly myPlayerId: string | null;
}) {
  const visible =
    state.round.phase !== "BETTING" || state.status !== "COMPLETED";
  if (!visible) {
    return (
      <section
        aria-hidden="true"
        className="indian-poker-message empty"
        data-visible="false"
        data-testid="indian-poker-action-message"
      />
    );
  }

  const availableActions = getAvailableActionsText(state);
  const lastAction = getLastBettingActionText(state, getPlayerName, myPlayerId);
  const title =
    state.round.phase === "BETTING"
      ? isMyTurn
        ? "내 액션 차례입니다"
        : `${activeName}님 액션 차례입니다`
      : getResultText(state);
  const message =
    state.round.phase === "BETTING"
      ? lastAction
        ? `${lastAction} 이후 응답 대기 · ${availableActions}`
        : `이번 선택 · ${availableActions}`
      : state.round.phase === "ROUND_RESULT"
        ? "두 플레이어의 다음 라운드 준비를 기다립니다."
        : "게임이 종료되었습니다.";

  return (
    <section
      className="indian-poker-message"
      data-my-turn={isMyTurn ? "true" : "false"}
      data-visible="true"
      data-testid="indian-poker-action-message"
    >
      <strong>{title}</strong>
      <span>{message}</span>
    </section>
  );
}

function GameOverDialog({
  state,
  getPlayerName,
  myPlayerId,
  onLeave,
  leaveLabel = "로비로 나가기",
}: {
  readonly state: IndianPokerViewerState;
  readonly getPlayerName: PlayerNameResolver;
  readonly myPlayerId: string | null;
  readonly onLeave: () => void;
  readonly leaveLabel?: string;
}) {
  const winner = state.winnerPlayerId
    ? (state.players.find(
        (player) => player.playerId === state.winnerPlayerId,
      ) ?? null)
    : null;
  const winnerName = winner ? getPlayerName(winner.playerId) : "승자 없음";
  const sortedPlayers = [...state.players].sort((a, b) => b.chips - a.chips);
  const isMyWin = winner?.playerId === myPlayerId;

  return (
    <div
      aria-labelledby="indian-poker-game-over-title"
      aria-modal="true"
      className="indian-poker-game-over-backdrop"
      data-testid="indian-poker-game-over-dialog"
      role="dialog"
    >
      <section className="indian-poker-game-over-panel">
        <span className="indian-poker-pill">게임 종료</span>
        <div className="indian-poker-game-over-hero">
          <h2
            data-testid="indian-poker-game-over-winner"
            id="indian-poker-game-over-title"
          >
            {winner ? `${winnerName} 승리` : "승자 없음"}
          </h2>
          <p>{isMyWin ? "최종 승리했습니다" : "최종 결과가 결정되었습니다"}</p>
        </div>

        <div className="indian-poker-game-over-summary">
          {sortedPlayers.map((player) => {
            const isWinner = player.playerId === winner?.playerId;
            const playerName = getPlayerName(player.playerId);
            return (
              <div
                className={`indian-poker-game-over-row ${
                  isWinner ? "winner" : ""
                }`}
                data-testid={`indian-poker-game-over-player-${player.playerId}`}
                key={player.playerId}
              >
                <div>
                  <strong>{playerName}</strong>
                  {isWinner && <span>우승자</span>}
                </div>
                <span>
                  <strong className="indian-poker-number">
                    {player.chips}
                  </strong>
                  칩
                </span>
              </div>
            );
          })}
        </div>

        <button
          className="indian-poker-button primary"
          data-testid="indian-poker-leave-game"
          onClick={onLeave}
          type="button"
        >
          {leaveLabel}
        </button>
      </section>
    </div>
  );
}

function clampRaiseTotal(
  totalBet: number,
  minRaiseTotal: number,
  maxRaiseTotal: number,
): number {
  return Math.min(maxRaiseTotal, Math.max(minRaiseTotal, totalBet));
}

function ActionDock({
  state,
  self,
  opponent,
  isMyTurn,
  readOnly,
}: {
  readonly state: IndianPokerViewerState;
  readonly self: PlayerView | null;
  readonly opponent: PlayerView | null;
  readonly isMyTurn: boolean;
  readonly readOnly: boolean;
}) {
  const [raiseMode, setRaiseMode] = useState(false);
  const [selectedRaiseTotal, setSelectedRaiseTotal] = useState(0);
  const [unlockedResultKey, setUnlockedResultKey] = useState("");
  const selfBet = self?.player.currentBet ?? 0;
  const selfChips = self?.player.chips ?? 0;
  const opponentBet = opponent?.player.currentBet ?? 0;
  const requiredCallAmount = Math.max(0, opponentBet - selfBet);
  const minRaiseTotal = opponentBet + 1;
  const maxRaiseTotal = Math.min(
    selfBet + selfChips,
    opponentBet + (opponent?.player.chips ?? 0),
  );
  const raiseStackStep = Math.max(1, state.round.pot);
  const hasSelectedRaise = selectedRaiseTotal > 0;
  const raiseTotal = hasSelectedRaise
    ? clampRaiseTotal(selectedRaiseTotal, minRaiseTotal, maxRaiseTotal)
    : 0;
  const selectedRaiseAmount = hasSelectedRaise
    ? Math.max(0, raiseTotal - opponentBet)
    : 0;
  const canOpenRaise = isMyTurn && minRaiseTotal <= maxRaiseTotal;
  const canRaise =
    hasSelectedRaise &&
    canOpenRaise &&
    Number.isInteger(raiseTotal) &&
    raiseTotal >= minRaiseTotal &&
    raiseTotal <= maxRaiseTotal;
  const canCall =
    isMyTurn && requiredCallAmount > 0 && requiredCallAmount <= selfChips;
  const canFold = isMyTurn;
  const actionHint = canCall
    ? canOpenRaise
      ? "콜 / 레이즈 / 폴드"
      : "콜 / 폴드"
    : canOpenRaise
      ? "레이즈 또는 폴드"
      : "자동 공개 대기";
  const confirmed = self
    ? state.round.confirmedPlayerIds.includes(self.player.playerId)
    : false;
  const resultRevealKey =
    state.round.phase === "ROUND_RESULT"
      ? `${state.round.roundNumber}:${state.round.resultReason ?? "result"}`
      : "";
  const resultRevealLocked =
    state.round.phase === "ROUND_RESULT" &&
    unlockedResultKey !== resultRevealKey;
  const resultActionLabel = willShowGameOverAfterResult(state)
    ? "최종 결과"
    : "다음 라운드";

  // biome-ignore lint/correctness/useExhaustiveDependencies: close the transient raise selector when the round or phase changes, even if the minimum raise total repeats.
  useEffect(() => {
    setSelectedRaiseTotal(0);
    setRaiseMode(false);
  }, [minRaiseTotal, state.round.roundNumber, state.round.phase]);

  useEffect(() => {
    if (!isMyTurn) {
      setRaiseMode(false);
    }
  }, [isMyTurn]);

  useEffect(() => {
    if (resultRevealKey.length === 0) {
      setUnlockedResultKey("");
      return;
    }

    setUnlockedResultKey("");
    const timerId = globalThis.setTimeout(() => {
      setUnlockedResultKey(resultRevealKey);
    }, ROUND_RESULT_REVEAL_LOCK_MS);

    return () => globalThis.clearTimeout(timerId);
  }, [resultRevealKey]);

  function adjustRaiseTotal(delta: number): void {
    setSelectedRaiseTotal((current) => {
      const baseTotal = current > 0 ? current : opponentBet;
      const nextTotal = Math.max(0, baseTotal + delta);
      if (nextTotal === 0) {
        return 0;
      }
      return clampRaiseTotal(nextTotal, minRaiseTotal, maxRaiseTotal);
    });
  }

  if (state.round.phase === "ROUND_RESULT") {
    return (
      <section
        className="indian-poker-actions"
        data-mode="round-result"
        data-testid="indian-poker-action-dock"
      >
        <span
          className="indian-poker-pill"
          data-testid="indian-poker-result-text"
        >
          {getResultText(state)}
        </span>
        <button
          className="indian-poker-button primary"
          data-testid="indian-poker-confirm-result"
          type="button"
          disabled={readOnly || confirmed || resultRevealLocked}
          onClick={() =>
            void indianPokerMultiplayerActions.submitAction(
              "CONFIRM_ROUND_RESULT",
            )
          }
        >
          {resultRevealLocked
            ? "공개 중"
            : confirmed
              ? "상대방 대기중"
              : resultActionLabel}
        </button>
      </section>
    );
  }

  if (state.round.phase === "GAME_OVER") {
    return (
      <section
        className="indian-poker-actions"
        data-mode="game-over"
        data-testid="indian-poker-action-dock"
      >
        <span className="indian-poker-pill">최종 결과</span>
        <p
          className="indian-poker-muted"
          data-testid="indian-poker-result-text"
        >
          {getResultText(state)}
        </p>
      </section>
    );
  }

  if (raiseMode) {
    return (
      <section
        className="indian-poker-actions"
        data-mode="raise"
        data-my-turn={isMyTurn ? "true" : "false"}
        data-testid="indian-poker-action-dock"
      >
        <span className="indian-poker-pill">레이즈 금액 선택</span>
        <div
          className="indian-poker-raise-panel"
          data-testid="indian-poker-raise-panel"
        >
          <div className="indian-poker-raise-head">
            <span>레이즈 조절</span>
            <strong data-testid="indian-poker-raise-total">
              총 {raiseTotal}칩
            </strong>
          </div>
          <div
            className="indian-poker-raise-options"
            data-stack-step={raiseStackStep}
          >
            <button
              aria-label="레이즈 초기화"
              className="indian-poker-raise-option reset"
              data-testid="indian-poker-raise-reset"
              type="button"
              disabled={!hasSelectedRaise}
              onClick={() => setSelectedRaiseTotal(0)}
            >
              ↻
            </button>
            <span className="indian-poker-raise-divider" aria-hidden="true">
              ||
            </span>
            <button
              className="indian-poker-raise-option"
              data-testid="indian-poker-raise-minus-stack"
              type="button"
              disabled={!hasSelectedRaise || raiseTotal <= minRaiseTotal}
              onClick={() => adjustRaiseTotal(-raiseStackStep)}
            >
              -{raiseStackStep}
            </button>
            <button
              className="indian-poker-raise-option"
              data-testid="indian-poker-raise-minus-1"
              type="button"
              disabled={!hasSelectedRaise || raiseTotal <= minRaiseTotal}
              onClick={() => adjustRaiseTotal(-1)}
            >
              -1
            </button>
            <button
              className="indian-poker-raise-option"
              data-testid="indian-poker-raise-plus-1"
              type="button"
              disabled={
                !canOpenRaise ||
                (hasSelectedRaise && raiseTotal >= maxRaiseTotal)
              }
              onClick={() => adjustRaiseTotal(1)}
            >
              +1
            </button>
            <button
              className="indian-poker-raise-option"
              data-testid="indian-poker-raise-plus-stack"
              type="button"
              disabled={
                !canOpenRaise ||
                (hasSelectedRaise && raiseTotal >= maxRaiseTotal)
              }
              onClick={() => adjustRaiseTotal(raiseStackStep)}
            >
              +{raiseStackStep}
            </button>
          </div>
          <div className="indian-poker-action-row two">
            <button
              className="indian-poker-button"
              data-testid="indian-poker-raise-cancel"
              type="button"
              onClick={() => setRaiseMode(false)}
            >
              취소
            </button>
            <button
              className="indian-poker-button primary"
              data-testid="indian-poker-raise-submit"
              type="button"
              disabled={!canRaise}
              onClick={() => {
                setRaiseMode(false);
                void indianPokerMultiplayerActions.submitAction("RAISE", {
                  totalBet: raiseTotal,
                });
              }}
            >
              {hasSelectedRaise ? `${selectedRaiseAmount}칩 레이즈` : "레이즈"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="indian-poker-actions"
      data-mode="default"
      data-my-turn={isMyTurn ? "true" : "false"}
      data-testid="indian-poker-action-dock"
    >
      <span className="indian-poker-pill">
        {isMyTurn ? actionHint : "상대 선택 대기"}
      </span>
      <div className="indian-poker-action-row">
        <button
          aria-hidden={!canCall}
          className="indian-poker-button primary"
          data-slot-hidden={canCall ? undefined : "true"}
          data-testid="indian-poker-call"
          type="button"
          disabled={!canCall || readOnly}
          tabIndex={canCall ? undefined : -1}
          onClick={() =>
            void indianPokerMultiplayerActions.submitAction("CALL")
          }
        >
          {canCall ? `콜 (${requiredCallAmount}칩)` : "콜"}
        </button>
        <button
          aria-hidden={!canOpenRaise}
          className="indian-poker-button primary"
          data-slot-hidden={canOpenRaise ? undefined : "true"}
          data-testid="indian-poker-raise-open"
          type="button"
          disabled={!canOpenRaise || readOnly}
          tabIndex={canOpenRaise ? undefined : -1}
          onClick={() => {
            setSelectedRaiseTotal(0);
            setRaiseMode(true);
          }}
        >
          레이즈
        </button>
        <button
          className="indian-poker-button danger"
          data-testid="indian-poker-fold"
          type="button"
          disabled={!canFold || readOnly}
          onClick={() =>
            void indianPokerMultiplayerActions.submitAction("FOLD")
          }
        >
          폴드
        </button>
      </div>
    </section>
  );
}

function ObserverBottomSpace() {
  return (
    <>
      <section
        aria-hidden="true"
        className="indian-poker-message empty"
        data-visible="false"
        data-testid="indian-poker-action-message"
      />
      <section
        aria-hidden="true"
        className="indian-poker-actions empty"
        data-testid="indian-poker-action-dock"
      />
    </>
  );
}

export interface IndianPokerObserverViewProps {
  readonly viewerState: IndianPokerViewerState;
  readonly getPlayerName: PlayerNameResolver;
  readonly onLeave: () => void;
  readonly lastError?: string | null;
  readonly leaveLabel?: string;
}

export function IndianPokerObserverView({
  viewerState,
  getPlayerName,
  onLeave,
  lastError = null,
  leaveLabel = "닫기",
}: IndianPokerObserverViewProps) {
  const views = getObserverPlayerViews(viewerState);
  const selfBet = views.self?.player.currentBet ?? 0;
  const opponentBet = views.opponent?.player.currentBet ?? 0;
  const betGap = Math.abs(opponentBet - selfBet);
  const isBettingPhase = viewerState.round.phase === "BETTING";

  return (
    <main
      className="indian-poker-root"
      data-active-player-id={viewerState.round.activePlayerId}
      data-my-turn="false"
      data-phase={viewerState.round.phase}
      data-round-number={viewerState.round.roundNumber}
      data-testid="indian-poker-screen"
    >
      <div className="indian-poker-shell">
        <section className="indian-poker-table">
          <PlayerSeat
            owner="opponent"
            view={views.opponent}
            label="플레이어 2"
            name={
              views.opponent
                ? getPlayerName(views.opponent.player.playerId)
                : "대기"
            }
            active={
              isBettingPhase &&
              viewerState.round.activePlayerId ===
                views.opponent?.player.playerId
            }
            result={getSeatResult(viewerState, views.opponent?.player.playerId)}
          />
          <DecisionPanel
            state={viewerState}
            selfBet={selfBet}
            opponentBet={opponentBet}
            betGap={betGap}
            getPlayerName={getPlayerName}
            myPlayerId={null}
            isMyTurn={false}
          />
          <PlayerSeat
            owner="self"
            view={views.self}
            label="플레이어 1"
            name={
              views.self ? getPlayerName(views.self.player.playerId) : "관전 중"
            }
            active={
              isBettingPhase &&
              viewerState.round.activePlayerId === views.self?.player.playerId
            }
            observerStatus={true}
            result={getSeatResult(viewerState, views.self?.player.playerId)}
          />
        </section>

        <div className="indian-poker-bottom">
          <ObserverBottomSpace />
          {lastError && <p className="indian-poker-muted">{lastError}</p>}
        </div>
      </div>
      {viewerState.round.phase === "GAME_OVER" && (
        <GameOverDialog
          state={viewerState}
          getPlayerName={getPlayerName}
          myPlayerId={null}
          onLeave={onLeave}
          leaveLabel={leaveLabel}
        />
      )}
    </main>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError, myPlayerId, isObserver } =
    useIndianPokerMultiplayerStore();
  const actionsRef = useRef(indianPokerMultiplayerActions);
  const lobbyActionsRef = useRef(lobbyActions);
  const userId = user?.id ?? null;
  const roomId = currentRoom?.id ?? null;
  const hostUserId = currentRoom?.hostUserId ?? null;
  const participantUserIds = currentRoom?.participantUserIds ?? [];
  const participantUserIdsKey = participantUserIds.join("|");
  const roomSeed =
    currentRoom && roomId ? getRoomSeed(currentRoom.config, roomId) : null;
  const roomStartingChips = currentRoom
    ? getRoomStartingChips(currentRoom.config)
    : INDIAN_POKER_STARTING_CHIPS;
  const isHost = hostUserId === userId;
  const isRoomObserver = Boolean(
    userId &&
      (currentRoom?.observerUserIds ?? []).includes(userId) &&
      !participantUserIds.includes(userId),
  );
  const readOnlyObserver = isObserver || isRoomObserver;
  const isBettingPhase = viewerState?.round.phase === "BETTING";
  const resolvePlayerName: PlayerNameResolver = (playerId) =>
    getRoomPlayerName(currentRoom, playerId);

  useEffect(() => {
    actionsRef.current = indianPokerMultiplayerActions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  useGameEventsWS(roomId, {
    onGameEvent: () => {},
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  useEffect(() => {
    if (!roomId || !userId || !hostUserId || !roomSeed) return;
    const participantIds = participantUserIdsKey.split("|").filter(Boolean);
    if (participantIds.length !== 2) return;

    if (readOnlyObserver) {
      void indianPokerMultiplayerActions.initializeAsObserver(roomId);
    } else if (isHost) {
      void indianPokerMultiplayerActions.initializeAsHost(
        roomId,
        participantIds,
        hostUserId,
        roomSeed,
        roomStartingChips,
      );
    } else {
      void indianPokerMultiplayerActions.initializeAsGuest(roomId);
    }

    return () => {
      indianPokerMultiplayerActions.reset();
    };
  }, [
    roomId,
    userId,
    hostUserId,
    participantUserIdsKey,
    roomSeed,
    roomStartingChips,
    isHost,
    readOnlyObserver,
  ]);

  const views = viewerState
    ? readOnlyObserver
      ? getObserverPlayerViews(viewerState)
      : getPlayerViews(viewerState, myPlayerId)
    : { self: null, opponent: null };
  const selfBet = views.self?.player.currentBet ?? 0;
  const opponentBet = views.opponent?.player.currentBet ?? 0;
  const betGap = Math.abs(opponentBet - selfBet);
  const activeName =
    viewerState && currentRoom
      ? resolvePlayerName(viewerState.round.activePlayerId)
      : "상대";
  const isMyTurn =
    Boolean(viewerState && myPlayerId) &&
    viewerState?.round.phase === "BETTING" &&
    viewerState.round.activePlayerId === myPlayerId &&
    !readOnlyObserver;

  function leaveGame(): void {
    indianPokerMultiplayerActions.reset();
    void lobbyActions.leaveGame();
    navigateTo(buildLobbyPath(INDIAN_POKER_GAME_TYPE));
  }

  if (!currentRoom || !viewerState) {
    return (
      <main className="indian-poker-root" data-testid="indian-poker-loading">
        <div className="indian-poker-shell">
          <section className="indian-poker-actions">
            <span className="indian-poker-pill">인디언 포커</span>
            <p className="indian-poker-muted">
              게임 상태를 불러오는 중입니다. 2명이 모두 입장하면 라운드가
              시작됩니다.
            </p>
            {lastError && (
              <p className="indian-poker-muted">{lastError.error}</p>
            )}
            <button
              className="indian-poker-button"
              onClick={leaveGame}
              type="button"
            >
              로비로 이동
            </button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main
      className="indian-poker-root"
      data-active-player-id={viewerState.round.activePlayerId}
      data-my-turn={isMyTurn ? "true" : "false"}
      data-phase={viewerState.round.phase}
      data-round-number={viewerState.round.roundNumber}
      data-testid="indian-poker-screen"
    >
      <div className="indian-poker-shell">
        <section className="indian-poker-table">
          <PlayerSeat
            owner="opponent"
            view={views.opponent}
            label={readOnlyObserver ? "플레이어 2" : "상대"}
            name={
              views.opponent
                ? resolvePlayerName(views.opponent.player.playerId)
                : "대기"
            }
            active={
              isBettingPhase &&
              viewerState.round.activePlayerId ===
                views.opponent?.player.playerId
            }
            result={getSeatResult(viewerState, views.opponent?.player.playerId)}
          />
          <DecisionPanel
            state={viewerState}
            selfBet={selfBet}
            opponentBet={opponentBet}
            betGap={betGap}
            getPlayerName={resolvePlayerName}
            myPlayerId={readOnlyObserver ? null : myPlayerId}
            isMyTurn={isMyTurn}
          />
          <PlayerSeat
            owner="self"
            view={views.self}
            label={readOnlyObserver ? "플레이어 1" : "나"}
            name={
              views.self
                ? resolvePlayerName(views.self.player.playerId)
                : "관전 중"
            }
            active={
              isBettingPhase &&
              viewerState.round.activePlayerId === views.self?.player.playerId
            }
            observerStatus={readOnlyObserver}
            result={getSeatResult(viewerState, views.self?.player.playerId)}
          />
        </section>

        <div className="indian-poker-bottom">
          {readOnlyObserver ? (
            <ObserverBottomSpace />
          ) : (
            <>
              <ActionMessage
                state={viewerState}
                isMyTurn={isMyTurn}
                activeName={activeName}
                getPlayerName={resolvePlayerName}
                myPlayerId={myPlayerId}
              />
              <ActionDock
                state={viewerState}
                self={views.self}
                opponent={views.opponent}
                isMyTurn={isMyTurn}
                readOnly={false}
              />
            </>
          )}
          {lastError && <p className="indian-poker-muted">{lastError.error}</p>}
        </div>
      </div>
      {viewerState.round.phase === "GAME_OVER" && (
        <GameOverDialog
          state={viewerState}
          getPlayerName={resolvePlayerName}
          myPlayerId={readOnlyObserver ? null : myPlayerId}
          onLeave={leaveGame}
        />
      )}
    </main>
  );
}
